import { IAgent } from '@d-id/types';
import { createContext, ReactNode, useState } from 'react';
import { ALL_AGENTS, INSIGHTS_DATE_RANGE } from '../types';
import { getDateGivenRange } from '../utils';

export interface AgentInsightsContextType {
    dateRange: INSIGHTS_DATE_RANGE;
    setDateRange: (dateRange: INSIGHTS_DATE_RANGE, customFrom?: Date, customTo?: Date) => void;
    from: Date;
    to: Date;

    agent: IAgent;
    setAgent: (agent: IAgent) => void;
    agent_ids: string[];

    // topics filters
    topicsFilter: string[];
    setTopicsFilter: (topics: string[]) => void;
    intentsFilter: string[];
    setIntentsFilter: (intents: string[]) => void;

    // conversations
    conversationTopicFilter: string | undefined;
    setConversationTopicFilter: (topic: string | undefined) => void;
    conversationIntentFilter: string | undefined;
    setConversationIntentFilter: (intent: string | undefined) => void;

    isConversationView: boolean;

    shareId?: string;
    isPublic: boolean;
}

export const AgentInsightsContext = createContext<AgentInsightsContextType>({
    // general filters
    dateRange: INSIGHTS_DATE_RANGE.LAST_6_MONTHS,
    setDateRange: () => {},
    from: getDateGivenRange(new Date(), INSIGHTS_DATE_RANGE.LAST_6_MONTHS),
    to: new Date(),

    agent: ALL_AGENTS,
    setAgent: () => {},
    agent_ids: [],

    // topics filters
    topicsFilter: [],
    setTopicsFilter: () => {},
    intentsFilter: [],
    setIntentsFilter: () => {},

    // conversations
    conversationTopicFilter: undefined,
    setConversationTopicFilter: () => {},
    conversationIntentFilter: undefined,
    setConversationIntentFilter: () => {},

    isConversationView: false,

    shareId: undefined,
    isPublic: false,
});

interface AgentInsightsContextProviderProps {
    children: ReactNode;
    shareId?: string;
    isPublic?: boolean;
}

export const AgentInsightsContextProvider = ({ children, shareId, isPublic = false }: AgentInsightsContextProviderProps) => {
    const [dateRange, setDateRange] = useState<INSIGHTS_DATE_RANGE>(INSIGHTS_DATE_RANGE.LAST_30_DAYS);
    const [agent, setAgent] = useState<IAgent>(ALL_AGENTS);
    const [topicsFilter, setTopicsFilter] = useState<string[]>([]);
    const [intentsFilter, setIntentsFilter] = useState<string[]>([]);
    const [conversationTopicFilter, setConversationTopicFilter] = useState<string | undefined>(undefined);
    const [conversationIntentFilter, setConversationIntentFilter] = useState<string | undefined>(undefined);

    const [from, setFrom] = useState<Date>(getDateGivenRange(new Date(), dateRange));
    const [to, setTo] = useState<Date>(new Date());

    const handleSetDateRange = (range: INSIGHTS_DATE_RANGE, customFrom?: Date, customTo?: Date) => {
        setDateRange(range);
        if (range === INSIGHTS_DATE_RANGE.CUSTOM && customFrom && customTo) {
            setFrom(customFrom);
            setTo(customTo);
        } else if (range !== INSIGHTS_DATE_RANGE.CUSTOM) {
            setFrom(getDateGivenRange(new Date(), range));
            setTo(new Date());
        }
    };

    return (
        <AgentInsightsContext.Provider
            value={{
                dateRange,
                setDateRange: handleSetDateRange,
                from,
                to,
                agent_ids: agent.id ? [agent.id] : [],
                topicsFilter,
                setTopicsFilter,
                intentsFilter,
                setIntentsFilter,
                agent,
                setAgent,
                conversationTopicFilter,
                setConversationTopicFilter,
                conversationIntentFilter,
                setConversationIntentFilter,
                isConversationView: !!conversationTopicFilter && !!conversationIntentFilter,
                shareId,
                isPublic,
            }}>
            {children}
        </AgentInsightsContext.Provider>
    );
};
